/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationUserSessionIssuersSetAuthenticationHost } from "../funcs/organizationUserSessionIssuersSetAuthenticationHost.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { UserSessionIssuer } from "../models/components/usersessionissuer.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetOrganizationUserSessionIssuerAuthenticationHostRequest,
  SetOrganizationUserSessionIssuerAuthenticationHostSecurity,
} from "../models/operations/setorganizationusersessionissuerauthenticationhost.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetOrganizationUserSessionIssuerAuthenticationHostMutationVariables =
  {
    request: SetOrganizationUserSessionIssuerAuthenticationHostRequest;
    security?:
      | SetOrganizationUserSessionIssuerAuthenticationHostSecurity
      | undefined;
    options?: RequestOptions;
  };

export type SetOrganizationUserSessionIssuerAuthenticationHostMutationData =
  UserSessionIssuer;

export type SetOrganizationUserSessionIssuerAuthenticationHostMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setAuthenticationHost organizationUserSessionIssuers
 *
 * @remarks
 * Move a user_session_issuer in the caller's organization onto or off the deployment's authentication host. A shared authorization server with a pinned issuer URL is re-pinned to the matching host, so its issuer URL and token endpoint change; other issuers announce their authorization servers on the new host. Clients and anything configured with the previous URLs must be updated. Refused when the deployment has no authentication host, and for issuers managed by Speakeasy. Requires org:admin.
 */
export function useSetOrganizationUserSessionIssuerAuthenticationHostMutation(
  options?: MutationHookOptions<
    SetOrganizationUserSessionIssuerAuthenticationHostMutationData,
    SetOrganizationUserSessionIssuerAuthenticationHostMutationError,
    SetOrganizationUserSessionIssuerAuthenticationHostMutationVariables
  >,
): UseMutationResult<
  SetOrganizationUserSessionIssuerAuthenticationHostMutationData,
  SetOrganizationUserSessionIssuerAuthenticationHostMutationError,
  SetOrganizationUserSessionIssuerAuthenticationHostMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetOrganizationUserSessionIssuerAuthenticationHostMutation(
      client,
      options,
    ),
    ...options,
  });
}

export function mutationKeySetOrganizationUserSessionIssuerAuthenticationHost(): MutationKey {
  return [
    "@gram/client",
    "organizationUserSessionIssuers",
    "setAuthenticationHost",
  ];
}

export function buildSetOrganizationUserSessionIssuerAuthenticationHostMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables:
      SetOrganizationUserSessionIssuerAuthenticationHostMutationVariables,
  ) => Promise<SetOrganizationUserSessionIssuerAuthenticationHostMutationData>;
} {
  return {
    mutationKey:
      mutationKeySetOrganizationUserSessionIssuerAuthenticationHost(),
    mutationFn:
      function setOrganizationUserSessionIssuerAuthenticationHostMutationFn({
        request,
        security,
        options,
      }): Promise<
        SetOrganizationUserSessionIssuerAuthenticationHostMutationData
      > {
        const mergedOptions = {
          ...hookOptions,
          ...options,
          fetchOptions: {
            ...hookOptions?.fetchOptions,
            ...options?.fetchOptions,
            signal: combineSignals(
              hookOptions?.fetchOptions?.signal,
              options?.fetchOptions?.signal,
            ),
          },
        };
        return unwrapAsync(organizationUserSessionIssuersSetAuthenticationHost(
          client$,
          request,
          security,
          mergedOptions,
        ));
      },
  };
}
